import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { trackPurchase } from "@/utils/analytics";

type SessionData = {
  id: string;
  payment_status: string;
  customer_email: string;
  amount_total: number;
  currency: string;
  metadata: {
    items?: string;
  };
};

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentIntent = searchParams.get("payment_intent");

    // Handle new flow - payment_intent from programmatic redirect
    if (paymentIntent && !sessionId) {
      // Get order data from localStorage (set during checkout)
      const orderData = localStorage.getItem("last_order");
      
      if (orderData) {
        try {
          const order = JSON.parse(orderData);
          setSession({
            id: paymentIntent,
            payment_status: "paid",
            customer_email: order.customerEmail,
            amount_total: Math.round(order.orderTotal * 100), // Convert to cents
            currency: "gbp",
            metadata: {
              items: JSON.stringify(order.items),
            },
          });
          
          // Track purchase
          trackPurchase(
            paymentIntent,
            order.orderTotal,
            order.items.map((item: any) => ({
              item_id: item.name.toLowerCase().replace(/\s+/g, '-'),
              item_name: item.name,
              price: item.price || 0,
              quantity: item.quantity || 1,
            }))
          );
          
          // Clean up
          localStorage.removeItem("last_order");
          setLoading(false);
          return;
        } catch (err) {
          console.error("Error parsing order data:", err);
        }
      }
    }

    // Handle old flow - session_id from Stripe redirect
    if (sessionId) {
      const fetchSession = async () => {
        try {
          const { data, error } = await supabase.functions.invoke(
            "retrieve-checkout-session",
            {
              body: { sessionId },
            }
          );

          if (error) throw error;

          setSession(data);
          
          // Track purchase completion in GA4
          if (data && data.metadata?.items) {
            const items = parseItems(data.metadata);
            trackPurchase(
              sessionId,
              data.amount_total / 100, // Convert cents to pounds
              items.map((item: any) => ({
                item_id: item.name.toLowerCase().replace(/\s+/g, '-'),
                item_name: item.name,
                price: item.price || 0,
                quantity: item.quantity || 1,
              }))
            );
          }
        } catch (err) {
          console.error("Error fetching session:", err);
          setError("Failed to retrieve order details");
        } finally {
          setLoading(false);
        }
      };

      fetchSession();
      return;
    }

    // No session ID or payment intent found
    setError("No payment information found");
    setLoading(false);
  }, [searchParams]);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const parseItems = (metadata: SessionData["metadata"]) => {
    try {
      return metadata.items ? JSON.parse(metadata.items) : [];
    } catch {
      return [];
    }
  };

  return (
    <>
      <SEOHead
        title="Order Successful"
        description="Your order has been successfully processed"
        canonical="/checkout/success"
      />

      <Page>
        <section className="min-h-screen w-full flex items-center justify-center">
          <div className="max-w-2xl w-full mx-auto px-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-emerald-400" />
                <p className="text-slate-300">Loading order details...</p>
              </div>
            ) : error ? (
              <div className="rounded-3xl border border-red-500/30 bg-slate-900/70 p-8 text-center backdrop-blur-xl">
                <p className="text-red-400 mb-4">{error}</p>
                <Link to="/shop">
                  <Button className="rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium">
                    Return to shop
                  </Button>
                </Link>
              </div>
            ) : session ? (
              <div className="rounded-3xl border border-emerald-300/30 bg-slate-900/70 p-8 backdrop-blur-xl space-y-6">
                <div className="flex flex-col items-center text-center gap-4">
                  <CheckCircle2 className="h-16 w-16 text-emerald-400" />
                  <h1 className="text-3xl font-semibold text-emerald-50">
                    Payment successful!
                  </h1>
                  <p className="text-slate-300">
                    Thank you for your order. Your payment has been processed
                    successfully.
                  </p>
                </div>

                <div className="border-t border-slate-700/60 pt-6 space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Order ID</span>
                    <span className="text-slate-200 font-mono">{session.id}</span>
                  </div>

                  {session.customer_email && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Email</span>
                      <span className="text-slate-200">{session.customer_email}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Payment Status</span>
                    <span className="text-emerald-400 capitalize">
                      {session.payment_status}
                    </span>
                  </div>

                  {session.metadata.items && (
                    <div className="space-y-2">
                      <span className="text-slate-400 text-sm">Items</span>
                      <div className="space-y-2">
                        {parseItems(session.metadata).map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className="flex justify-between text-sm bg-slate-800/50 rounded-lg p-3"
                          >
                            <span className="text-slate-200">{item.name}</span>
                            <span className="text-slate-300">
                              Qty: {item.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-semibold pt-4 border-t border-slate-700/60">
                    <span className="text-slate-200">Total</span>
                    <span className="text-emerald-400">
                      {formatAmount(session.amount_total, session.currency)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-4">
                  <p className="text-sm text-slate-400 text-center">
                    A confirmation email has been sent to{" "}
                    {session.customer_email || "your email address"}.
                  </p>
                  <Link to="/shop" className="w-full">
                    <Button className="w-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium">
                      Continue shopping
                    </Button>
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </Page>
    </>
  );
}
